import { useEffect, useState } from 'react';
import { AlertTriangle, LogOut } from 'lucide-react';
import { useAuthStore } from '@/features/auth/store';

/**
 * Persistent red banner shown across every page during an impersonation
 * session. Mounted globally inside App so it appears whether the admin is
 * viewing owner pages, employee pages, or the profile.
 *
 * Also renders a countdown to the token's expiry — the token itself is
 * short-lived (30 min from mint) and refuses to refresh, so once it lapses
 * the app naturally kicks back to /login. The countdown surfaces that
 * timing to the admin so they don't get surprised mid-click.
 */
export function ImpersonationBanner() {
  const user = useAuthStore((s) => s.user);
  const expiresAt = useAuthStore((s) => s.accessTokenExpiresAt);
  const clear = useAuthStore((s) => s.clear);
  const [remaining, setRemaining] = useState(() => computeRemaining(expiresAt));

  useEffect(() => {
    if (!user?.impersonatedBy) return;
    const t = setInterval(() => setRemaining(computeRemaining(expiresAt)), 1000);
    return () => clearInterval(t);
  }, [user?.impersonatedBy, expiresAt]);

  if (!user?.impersonatedBy) return null;

  const exit = () => {
    // Impersonation lives in this tab's memory only — no cookie to clear.
    // Just wipe the store and close the tab; the tab-open flow was
    // window.open(...), so the tab is disposable.
    clear();
    window.close();
    // window.close only works on tabs opened by script; fall back to
    // redirecting to /login if the browser refuses.
    setTimeout(() => {
      if (!window.closed) window.location.href = '/login';
    }, 100);
  };

  return (
    <div
      role="alert"
      className="sticky top-0 z-[60] flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-destructive-border bg-destructive px-4 py-2 text-2xs font-semibold text-destructive-foreground"
    >
      <AlertTriangle className="h-3.5 w-3.5" />
      <span>
        VIEWING AS <span className="uppercase">{user.fullName}</span>
        <span className="mx-1.5 opacity-75">·</span>
        <span className="opacity-90">{user.tenantName}</span>
        <span className="mx-1.5 opacity-75">·</span>
        <span className="opacity-90">read-only support session</span>
      </span>
      <span className="rounded bg-black/25 px-1.5 py-0.5 font-mono">
        expires in {remaining}
      </span>
      <button
        onClick={exit}
        className="inline-flex items-center gap-1 rounded bg-black/25 px-2 py-0.5 hover:bg-black/40"
      >
        <LogOut className="h-3 w-3" />
        Exit
      </button>
    </div>
  );
}

function computeRemaining(iso: string | null): string {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
